/**
 * 官媒舱展示词表(console 侧单源)。
 *
 * 体裁/语气的**合法值**真源在后端(ReporterDraftService GENRES/TONES),这里只管中文显示名;
 * 字段短名对齐 gscq-ai `assets/genres.py FIELD_DESCRIPTIONS`(取括号前短语),
 * 未知 key 原样返回(诚实展示,不吞新值 —— moduleLabel 同范式)。
 */
import { humanizeInternalTerms } from "@/shared/lib/internalCodes";

import type { ReporterGenre, ReporterTone } from "../api/reporterService";
import type { MaterialStatus } from "./parse";

export const GENRE_LABEL: Record<ReporterGenre, string> = {
  COMPANY_NEWS: "企业新闻",
  PRODUCT_COPY: "产品软文",
  // 2026-08-10 维度转正:卡=体裁、渠道后置出口,显示名脱渠道(genre 代码不动)。
  XIAOHONGSHU: "种草笔记",
  EVENT_RELEASE: "活动通稿",
  BRAND_PROFILE: "品牌介绍",
  QA_ARTICLE: "GEO 问答文",
};

export const TONE_LABEL: Record<ReporterTone, string> = {
  PROFESSIONAL: "专业可信",
  FORMAL: "正式克制",
  FRIENDLY: "亲和",
  MARKETING: "营销",
  LIVELY: "活泼",
};

/** 语气 chips 顺序(默认 PROFESSIONAL 在前,与后端缺省一致)。 */
export const TONE_ORDER: ReporterTone[] = [
  "PROFESSIONAL",
  "FORMAL",
  "FRIENDLY",
  "MARKETING",
  "LIVELY",
];

/**
 * 品牌介绍四子类(设计稿「品牌·四子类」)。code 走 CreateDraftRequest.subType ——
 * 后端 ReporterDraftService 只认 COMPANY_INTRO/TEAM_INTRO/FOUNDER_PROFILE/BRAND_STORY,
 * ai 体裁提示词(genres.py)也按这四个码分写法;label 只给页面显示。
 * (1003 B1:此前直接发中文值,建稿必报 REPORTER_INVALID_PARAMS;军师路径一直发英文码。)
 */
export const BRAND_SUBTYPES = [
  { code: "COMPANY_INTRO", label: "公司简介" },
  { code: "TEAM_INTRO", label: "团队介绍" },
  { code: "FOUNDER_PROFILE", label: "创始人故事" },
  { code: "BRAND_STORY", label: "品牌故事" },
] as const;

export type BrandSubTypeCode = (typeof BRAND_SUBTYPES)[number]["code"];

/** 中文别名(场景卡 preset / 老数据可能带中文)→ 英文码。 */
const BRAND_SUBTYPE_ALIAS: Record<string, BrandSubTypeCode> = {
  公司简介: "COMPANY_INTRO",
  团队介绍: "TEAM_INTRO",
  创始人故事: "FOUNDER_PROFILE",
  创始人: "FOUNDER_PROFILE",
  品牌故事: "BRAND_STORY",
};

/** 任意来源的子类值归一成后端认的英文码;认不出返回 ""(= 不带子类,不让建稿被拒)。 */
export function normalizeBrandSubType(v: string | null | undefined): BrandSubTypeCode | "" {
  if (!v) return "";
  const t = v.trim();
  const hit = BRAND_SUBTYPES.find((s) => s.code === t);
  if (hit) return hit.code;
  return BRAND_SUBTYPE_ALIAS[t] ?? "";
}

/** 整理分级三色(实习生 explicit/inferred/uncertain 口径沿用)。 */
export const MATERIAL_STATUS_META: Record<MaterialStatus, { label: string; className: string }> = {
  explicit: { label: "原文明确", className: "bg-success/15 text-success" },
  inferred: { label: "合理推断", className: "bg-warning/15 text-warning" },
  uncertain: { label: "不确定", className: "bg-muted text-muted-foreground" },
};

/** 体裁字段短名(对齐 ai FIELD_DESCRIPTIONS 括号前短语;未知 key 原样返回)。 */
const FIELD_LABEL: Record<string, string> = {
  newsEvent: "新闻事件",
  companyAction: "企业行动",
  relatedBusiness: "涉及业务",
  industryValue: "行业价值",
  quote: "引用发言",
  productName: "产品名称",
  painPoints: "用户痛点",
  useCases: "使用场景",
  advantages: "差异优势",
  topic: "主题",
  experience: "使用体验",
  recommendReasons: "推荐理由",
  fitAudience: "适合人群",
  eventName: "活动名称",
  eventTime: "活动时间",
  eventLocation: "活动地点",
  organizer: "主办方",
  eventHighlights: "活动亮点",
  agenda: "议程安排",
  participants: "参与嘉宾",
  profileType: "介绍子类",
  organizationName: "组织名称",
  industry: "所属行业",
  businessScope: "主营业务",
  foundingBackground: "成立背景",
  coreCapabilities: "核心能力",
  teamStrengths: "团队优势",
  keyPeople: "关键人物",
  achievements: "代表成果",
  brandValues: "品牌理念",
  milestones: "发展里程碑",
  targetAudience: "面向对象",
  usageScenario: "使用场景",
  toneRequirement: "表达风格",
  userQuestions: "用户常见问题",
  name: "名称",
  targetUsers: "目标用户",
  sellingPoints: "核心卖点",
  background: "背景信息",
  extraRequirements: "补充要求",
  notes: "注意事项",
  tagDirection: "标签方向",
  // lexicon NO_INFERENCE_FIELDS 里两枚缺失提示常客(整理侧只出现在 missingFields)
  price: "产品定价",
  contact: "联系方式",
};

/**
 * 字段短名。1004 T10:后端偶尔给组合键(「brandValues / usageScenario」)或表外新键,
 * 原样返回会把驼峰字段名露给用户 —— 组合键逐段换;换不了的驼峰键统一显示「补充信息」。
 */
export function fieldLabel(key: string): string {
  const hit = FIELD_LABEL[key];
  if (hit) return hit;
  const replaced = key.replace(/[A-Za-z]+/g, (w) => FIELD_LABEL[w] ?? w);
  return /[a-z][A-Z]/.test(replaced) ? "补充信息" : replaced;
}

/** 依据来源标(evidenceMap.origin;与设计稿「用户素材/产品知识」口径一致)。 */
export const EVIDENCE_ORIGIN_LABEL: Record<string, string> = {
  material: "用户素材",
  product: "产品知识",
  enterprise: "企业配置",
  knowledge: "企业知识库",
};

/**
 * 质量门禁三档的对客名(1004 T10:页面上原样露出 PASS / WARNING / BLOCKED)。
 * 码本身仍留在 data-reporter-gate 属性上给测试 / 埋点用,不进可见文字。
 */
export const GATE_LABEL: Record<string, string> = {
  PASS: "可交付",
  WARNING: "需明显修改",
  BLOCKED: "不放行",
};

export function gateLabel(gate: string | null | undefined): string {
  const g = (gate ?? "").trim().toUpperCase();
  return GATE_LABEL[g] ?? (g ? "待评估" : "—");
}

/** 提醒严重度对客名(HIGH / MEDIUM / INFO 原样露出过)。 */
export const SEVERITY_LABEL: Record<string, string> = {
  HIGH: "高风险",
  MEDIUM: "需注意",
  LOW: "提示",
  INFO: "提示",
};

export function severityLabel(sev: string | null | undefined): string {
  const s = (sev ?? "").trim().toUpperCase();
  return SEVERITY_LABEL[s] ?? "提示";
}

/** 体裁码 + 子类码 → 中文(后端 / 模型文案里混着码,如「被判成 COMPANY_INTRO」)。 */
const REPORTER_CODE_LABEL: Record<string, string> = {
  ...GENRE_LABEL,
  ...GATE_LABEL,
  ...Object.fromEntries(BRAND_SUBTYPES.map((s) => [s.code, s.label])),
};

const CODE_RE = new RegExp(`\\b(${Object.keys(REPORTER_CODE_LABEL).join("|")})\\b`, "g");
const FIELD_RE = new RegExp(
  `\\b(${Object.keys(FIELD_LABEL)
    .filter((k) => /[A-Z]/.test(k)) // 只换驼峰字段名;name / topic / quote 这类英文常用词不动
    .join("|")})\\b`,
  "g"
);
const RISK_WORD_RE = /\b(HIGH|MEDIUM)\s*提醒/g;

/**
 * 新闻舱后端 / 模型原文对客清洗:体裁/子类码换中文、驼峰字段名换短名、「HIGH 提醒」换中文,
 * 再走通用的 {@link humanizeInternalTerms}。用在素材表取值 / 来源、质检提醒、未过规则等直出位置。
 */
export function humanizeReporterText(text: string | null | undefined): string {
  const replaced = (text ?? "")
    .replace(CODE_RE, (m) => REPORTER_CODE_LABEL[m] ?? m)
    .replace(FIELD_RE, (m) => FIELD_LABEL[m] ?? m)
    .replace(RISK_WORD_RE, (_m, s: string) => `${severityLabel(s)}提醒`);
  return humanizeInternalTerms(replaced);
}

/**
 * 写作规划「依据地图」一条的对客文字(1004 F23 #6)。
 * 演示机实测露过:`介绍类型:BRAND_STORY`、`foundingBackground:创立于…`、teamStrengths / keyPeople /
 * achievements / brandValues / milestones / usageScenario —— 后端把素材键与子类码原样塞进 label / value。
 * label:码换中文后再按字段短名表换(表外驼峰键给「补充信息」,不露原键);value:走新闻舱原文清洗。
 */
export function evidenceLine(e: { label: string; value: string }): string {
  // value 自带「键:值」前缀(achievements / milestones 这类全小写键 FIELD_RE 不换)→ 拆出来当 label
  //   只认字段表里的键,「iPhone:…」这类正常英文开头不动
  const m0 = !e.label ? /^\s*([A-Za-z][A-Za-z_]*)\s*[::]\s*/.exec(e.value) : null;
  const m = m0 && FIELD_LABEL[m0[1]] ? m0 : null;
  const rawLabel = e.label || (m ? m[1] : "");
  const rawValue = m ? e.value.slice(m[0].length) : e.value;
  const label = rawLabel ? fieldLabel(humanizeReporterText(rawLabel)) : "";
  const value = humanizeReporterText(rawValue);
  return label ? `${label}:${value}` : value;
}
